import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Stack, Tabs, Text, Title } from "@mantine/core";
import { toast } from "sonner";
import { billingApi, type PortalInvoice } from "@/lib/api/billing";
import { downloadInvoicePdf, saveBlob } from "@/lib/invoices";
import { InvoicesBody } from "@/components/invoices/invoices-view";
import { CompanyBookingCosts } from "@/components/invoices/booking-costs";

export default function InvoicesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const tab = location.pathname.startsWith("/invoices/booking-costs") ? "booking-costs" : "invoices";
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["portal-invoices"],
    queryFn: () => billingApi.listInvoices().then((r) => r.data),
    enabled: tab === "invoices",
    retry: (count, err) => (err as { response?: { status?: number } })?.response?.status !== 403 && count < 2,
  });
  const status = (error as { response?: { status?: number } } | null)?.response?.status;

  const onPdf = async (invoice: PortalInvoice) => {
    if (downloadingId !== null) return; // one at a time: each fetch counts against the rate limit
    setDownloadingId(invoice.id);
    const message = await downloadInvoicePdf(invoice, { fetchPdf: billingApi.getInvoicePdf, save: saveBlob });
    setDownloadingId(null);
    if (message) {
      Sentry.addBreadcrumb({ category: "invoices", message: `PDF of invoice ${invoice.id} failed` });
      toast.error(message);
    }
  };

  return (
    <Stack p="md" gap="lg">
      <Stack gap={4}>
        <Title order={2}>Invoices</Title>
        <Text fz="sm" c="dimmed">
          {tab === "invoices"
            ? "Your company's invoices and credit notes. Amounts include VAT."
            : "What your company's meeting-room bookings cost, why, and which invoice they are on. Amounts exclude VAT."}
        </Text>
      </Stack>
      <Tabs value={tab} onChange={(value) => navigate(value === "booking-costs" ? "/invoices/booking-costs" : "/invoices")}>
        <Tabs.List>
          <Tabs.Tab value="invoices">Invoices</Tabs.Tab>
          <Tabs.Tab value="booking-costs">Booking costs</Tabs.Tab>
        </Tabs.List>
      </Tabs>
      {tab === "invoices" ? (
        <InvoicesBody
          invoices={data}
          isLoading={isLoading}
          error={error ? (status ?? true) : null}
          downloadingId={downloadingId}
          onPdf={onPdf}
        />
      ) : (
        <CompanyBookingCosts />
      )}
    </Stack>
  );
}
